import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';

// Resource events are the highest-volume RUM event type, so we drop the ones
// whose count is unbounded per view — avatars, message attachments, icon fonts,
// audio/video — and which never explain why a screen is slow. What's left (xhr,
// fetch, js, css, other) is bounded per route and is exactly the waterfall we
// diagnose with: API latency plus the microfrontend chunks and stylesheets.
// Discarding happens in `beforeSend`, before the event leaves the browser, so
// nothing dropped here is ever ingested.
const EXCLUDED_RESOURCE_TYPES = new Set(['image', 'font', 'media']);

const FIRST_PARTY_DOMAINS = ['cloudhumans.com', 'cloudhumans.io'];

const isFirstPartyHost = hostname =>
  FIRST_PARTY_DOMAINS.some(
    domain => hostname === domain || hostname.endsWith(`.${domain}`)
  );

const isFirstPartyResourceUrl = url => {
  try {
    return isFirstPartyHost(new URL(url, window.location.href).hostname);
  } catch {
    return false;
  }
};

const isResourceTracked = event => {
  if (EXCLUDED_RESOURCE_TYPES.has(event.resource?.type)) return false;
  if (!event.resource?.url) return true;

  return isFirstPartyResourceUrl(event.resource.url);
};

// Rendered as a real JS boolean by the layout, but tolerate the string form so a
// hand-set InstallationConfig value doesn't silently read as falsy.
const trackResourcesEnabled = () =>
  window.datadogConfig.dd_rum_track_resources === true ||
  window.datadogConfig.dd_rum_track_resources === 'true';

const isFirstPartyRequest = url =>
  new URL(url, window.location.href).origin === window.location.origin;

export const initializeDatadogRum = () => {
  if (!window.datadogConfig || !window.datadogConfig.dd_rum_client_token) {
    // eslint-disable-next-line no-console
    console.warn(
      'Datadog RUM is not configured. Skipping initialization.',
      window.datadogConfig
    );
    return;
  }

  datadogRum.init({
    applicationId: window.datadogConfig.dd_rum_application_id,
    clientToken: window.datadogConfig.dd_rum_client_token,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: 'chatwoot-saas-browser',
    env: window.datadogConfig.dd_env || 'development',
    version: window.datadogConfig.dd_version || '1.0.0',

    // 🔪 trims cost and noise
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0, // replay OFF
    trackResources: trackResourcesEnabled(),
    trackLongTasks: true,
    trackUserInteractions: false,

    allowedTracingUrls: [isFirstPartyRequest],
    traceSampleRate: 100,

    beforeSend: event => event.type !== 'resource' || isResourceTracked(event),

    // optional, but helps with GDPR at no real cost
    defaultPrivacyLevel: 'mask-user-input',
  });

  datadogRum.setGlobalContextProperty(
    'instance_id',
    window.datadogConfig.instance_id
  );
  datadogRum.setGlobalContextProperty(
    'kube_namespace',
    window.datadogConfig.kube_namespace
  );

  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.isAxiosError && error.response === undefined) {
        datadogRum.addError(error, {
          message: error.message,
          url: error.config.url,
          method: error.config.method,
        });
      }
      return Promise.reject(error);
    }
  );

  datadogRum.startSessionReplayRecording();
};

export const setDatadogRumUser = user => {
  if (!window.datadogConfig || !window.datadogConfig.dd_rum_client_token) {
    // eslint-disable-next-line no-console
    console.warn(
      'Datadog RUM is not configured. Skipping user set.',
      window.datadogConfig
    );
    return;
  }
  datadogRum.setUser({
    id: user.pubsub_token,
    pubsub_token: user.pubsub_token,
    name: user.name,
    email: user.email,
    type: user.type,
    role: user.role,
    account_id: user.account_id,
  });
};
